// WHATSAPP-OTP-STANDARD-001: Der Code-Schritt der SMS-Zwei-Faktor-Anmeldung ist dieselbe /login-Anfrage
// mit genau einem Zusatzfeld — `sms_code` (Code absenden; prüft der Server gegen SMS- und E-Mail-Code),
// `two_factor_channel: 'email'` (E-Mail-Code erneut) oder `channel: 'sms'` („Lieber per SMS").
// Andere Felder werden nie übernommen; ohne Zusatz bleibt der Körper byte-gleich wie bisher.
export function createPartnerLoginBody(email, password, turnstileToken, extra = null) {
  const body = {
    email,
    password,
    role: 'partner',
    turnstile_token: turnstileToken,
  };
  if (typeof extra?.sms_code === 'string' && extra.sms_code) body.sms_code = extra.sms_code;
  if (extra?.two_factor_channel === 'email') body.two_factor_channel = 'email';
  if (extra?.channel === 'sms') body.channel = 'sms';
  return body;
}

// Kleine imperative Grenze, damit Tokenverbrauch und Doppeltap-Sperre synchron erfolgen.
// Der Token bleibt intern und erscheint nie im UI-Snapshot oder in Logs.
export function createTurnstileLoginState({ rebuildWidget = () => {} } = {}) {
  let token = null;
  let widgetRun = 0;
  let submitting = false;
  let retryReason = null;
  let rebuild = typeof rebuildWidget === 'function' ? rebuildWidget : () => {};
  const listeners = new Set();

  const snapshot = () => Object.freeze({
    widgetRun,
    submitting,
    tokenReady: Boolean(token),
    canSubmit: Boolean(token) && !submitting,
    retryReason,
  });
  const publish = () => {
    const next = snapshot();
    for (const listener of listeners) listener(next);
  };
  const advanceRun = (reason, finishSubmission, shouldRebuild) => {
    token = null;
    widgetRun += 1;
    retryReason = reason || null;
    if (finishSubmission) submitting = false;
    publish();
    if (shouldRebuild) rebuild(widgetRun);
  };

  return {
    snapshot,
    currentRun: () => widgetRun,
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    setRebuildWidget(nextRebuild) {
      rebuild = typeof nextRebuild === 'function' ? nextRebuild : () => {};
    },
    beginWidgetRender() {
      advanceRun(null, false, false);
      return widgetRun;
    },
    abandonWidget() {
      advanceRun(null, false, false);
    },
    tokenReceived(nextToken, run) {
      if (run !== widgetRun || submitting || typeof nextToken !== 'string' || !nextToken.trim()) return false;
      token = nextToken;
      retryReason = null;
      publish();
      return true;
    },
    beginSubmit() {
      if (submitting || !token) return null;
      const attempt = { token, widgetRun };
      token = null; // Einmal-Token synchron verbrauchen, bevor der Request startet.
      submitting = true;
      retryReason = null;
      publish();
      return attempt;
    },
    loginSucceeded() {
      token = null;
      submitting = false;
      retryReason = null;
      publish();
    },
    loginFailed(_status) {
      advanceRun('login-failed', true, true);
    },
    widgetInvalidated(reason, run, { requestRebuild = true } = {}) {
      if (run !== widgetRun) return false;
      const safeReason = reason === 'expired' ? 'expired' : 'widget-error';
      // Läuft bereits ein Auth-Request, bleibt submitting=true: ein Callback darf die
      // Doppeltap-Sperre nicht vor Abschluss des Requests lösen.
      advanceRun(safeReason, false, requestRebuild);
      return true;
    },
    retryWidget() {
      if (!submitting) advanceRun('manual-retry', false, true);
    },
  };
}
